import { ChangeDetectionStrategy, Component, computed, effect, inject, untracked } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { EvSalesmapLayoutComponent } from "src/app/shared-components/ev-salesmap-layout/ev-salesmap-layout.component";
import { NewDwellingsFilterComponent } from "../new-dwellings-filter/new-dwellings-filter.component";
import { NewDwellingsListComponent } from "./new-dwellings-list/new-dwellings-list.component";
import { NewDwellingStageInfocardComponent } from "./new-dwellings-list/new-dwelling-stage-infocard.component";
import { NewDwellingSalesDataService } from "src/services/new-dwellings/new-dwelling-sales-data.service";
import { toSignal } from "@angular/core/rxjs-interop";
import { NewDwellingMapComponentsService } from "src/services/new-dwellings/new-dwelling-map-components.service";
import { RightSidebarOptions, RightSidebarService } from "src/services/right-sidebar.service";
import { MapRightMenuButtonsComponent } from "src/app/shared-components/map-right-menu-buttons/map-right-menu-buttons.component";
import { AreaBuilderSidebarComponentV2 } from "../../shared-components/area-builder-sidebar/area-builder-sidebar-v2.component";
import { AreaBuilderDataService } from "src/services/area-builder-data.service";
import { AreaStructure } from "src/models/area-builder/area-structure.model";
import { Observable } from "rxjs/internal/Observable";
import { take } from "rxjs";
import { AreaType } from "src/models/area-builder/area-type.enum";
import { QueryArea } from "src/models/area-builder/query-area.model";
import { NewDwellingStatisticsComponent } from "../new-dwelling-statistics/new-dwelling-statistics.component";
import { MapLoadingOverlayComponent } from "../../shared-components/map-loading-overlay/map-loading-overlay.component";
import { NewDwellingDataService } from "src/app/infopage/services/new-dwelling-data.service";
import { MapActionsService } from "src/services/map/map-actions.service";
import { IProjectStageDto } from "src/models/new-dwelling/new-dwelling.model";
import { MapMode, MapStateService } from "src/services/map/map-state.service";
import { NewDwellingListsComponent } from "../new-dwelling-lists/new-dwelling-lists.component";
import { EvAuthorizationService } from "src/util/auth0/ev-auth.service";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";

@Component({
    selector: "new-dwelling-listings",
    imports: [
        AreaBuilderSidebarComponentV2,
        EvSalesmapLayoutComponent,
        NewDwellingsFilterComponent,
        NewDwellingsListComponent,
        NewDwellingStageInfocardComponent,
        MapRightMenuButtonsComponent,
        NewDwellingStatisticsComponent,
        MapLoadingOverlayComponent,
        NewDwellingListsComponent,
    ],

    templateUrl: "./new-dwelling-listings.component.html",
    styleUrl: "./new-dwelling-listings.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewDwellingListingsComponent {
    private readonly newDwellingDataService = inject(NewDwellingSalesDataService);
    private readonly newDwellingMapDataService = inject(NewDwellingMapComponentsService);
    private readonly rightSidebarService = inject(RightSidebarService);
    private readonly areaBuilderService = inject(AreaBuilderDataService);
    private readonly route = inject(ActivatedRoute);
    private readonly projectInfoService = inject(NewDwellingDataService);
    private readonly mapActionsService = inject(MapActionsService);
    private readonly mapStateService = inject(MapStateService);
    private readonly authService = inject(EvAuthorizationService);
    public readonly debugEnabled = this.authService.hasPermissionSignal(EVServiceType.Internal_Debug, false, false);

    sales = toSignal(this.newDwellingDataService.sales$);
    activeStage = this.newDwellingDataService.activeStage;
    protected readonly mapDataIsLoading = this.newDwellingDataService.mapDataIsLoading;
    selectedSidebar = this.rightSidebarService.selectedSidebar;
    rightSidebarOptions = RightSidebarOptions;
    areaStructure$: Observable<AreaStructure>;
    currentAreaStructure: AreaStructure;

    private rootQuery = { areaType: AreaType.Undefined, areaId: 0 };

    // Deep link: ?pid=<projectId>[&pseid=<projectStageEstateTypeId>][&lat=&lon=].
    private readonly initialProjectId = this.readPositiveInt("pid");
    private readonly initialStageEstateTypeId = this.readPositiveInt("pseid");
    private hasHandledInitialProject = false;

    constructor() {
        effect(() => this.newDwellingMapDataService.updateLayers(this.sales()?.projects));
        // MapActionsService clears selection markers on every map mode change. On a fresh load the project (from the URL)
        // is selected before the mode is set to NewDwellings, so re-add the marker once the mode is in place.
        effect(() => {
            const stage = this.activeStage();
            if (stage?.latitude != null && stage?.longitude != null && this.mapStateService.mapModeSignal() === MapMode.NewDwellings) {
                this.mapActionsService.addCurrentMarkerAt({ lat: stage.latitude, lng: stage.longitude });
            }
        });
        this.selectInitialProject();
        this.positionMapToInitialArea();
        this.fetchAreaStructure(this.rootQuery);
    }

    ngOnDestroy(): void {
        this.newDwellingMapDataService.cleanup();
    }

    fetchAreaStructure(query: QueryArea) {
        this.areaStructure$ = this.areaBuilderService.getAreaStructure(query);
        this.areaStructure$.subscribe((x) => (this.currentAreaStructure = x));
    }

    visibleButtons = computed(() =>
        this.debugEnabled()
            ? [this.rightSidebarOptions.Filter, this.rightSidebarOptions.AreaBuilder, this.rightSidebarOptions.Favorites]
            : [this.rightSidebarOptions.Filter, this.rightSidebarOptions.AreaBuilder],
    );

    private selectInitialProject(): void {
        const projectId = this.initialProjectId;
        if (!projectId) {
            this.hasHandledInitialProject = true;
            return;
        }

        const params = this.route.snapshot.queryParamMap;
        const lat = Number(params.get("lat"));
        const lon = Number(params.get("lon"));
        const hasUrlPosition = params.has("lat") && params.has("lon") && Number.isFinite(lat) && Number.isFinite(lon);

        if (hasUrlPosition) {
            this.newDwellingDataService.setSelectedStage(this.createProjectStub(projectId, lat, lon), this.initialStageEstateTypeId);
        } else {
            // No coordinates yet: load the project info so its position can be used before selecting it.
            this.projectInfoService.setProjectId(projectId);
            this.projectInfoService.setProjectStageEstateTypeId(this.initialStageEstateTypeId);
        }

        let isSelected = hasUrlPosition;

        effect(() => {
            if (this.hasHandledInitialProject) {
                return;
            }

            const activeStage = this.activeStage();
            const projectFromMap = this.sales()?.projects?.find((item) => item.projectId === projectId);

            if (!activeStage) {
                if (isSelected) {
                    this.hasHandledInitialProject = true;
                    return;
                }
                const position = this.projectInfoService.position();
                if (!position?.lat || !position?.lng) {
                    return;
                }
                isSelected = true;
                this.newDwellingDataService.setSelectedStage(projectFromMap ?? this.createProjectStub(projectId, position.lat, position.lng), this.initialStageEstateTypeId);
                this.mapActionsService.centerOn(position);
                this.hasHandledInitialProject = !!projectFromMap;
                return;
            }

            if (activeStage.projectId !== projectId) {
                this.hasHandledInitialProject = true;
                return;
            }

            if (projectFromMap) {
                this.hasHandledInitialProject = true;
                if (projectFromMap !== activeStage) {
                    const currentStageEstateTypeId = untracked(() => this.projectInfoService.pseid());
                    this.newDwellingDataService.setSelectedStage(projectFromMap, currentStageEstateTypeId);
                }
            }
        });
    }

    // Deep link: ?areaId=<id>&areaTypeId=<AreaType>. Ignored when a project is deep linked.
    private positionMapToInitialArea(): void {
        if (this.initialProjectId) {
            return;
        }

        const areaId = this.readPositiveInt("areaId");
        const areaTypeId = this.readPositiveInt("areaTypeId");
        if (!areaId || !areaTypeId) {
            return;
        }

        this.areaBuilderService
            .getAreaBoundary(areaId, areaTypeId)
            .pipe(take(1))
            .subscribe((area) => {
                // Just use geoborder for housingcooperativ
                if (areaTypeId === AreaType.HousingCooperative) {
                    this.areaBuilderService.positionMapToAreasAndPolygons([], area.geographicBorder);
                } else {
                    this.areaBuilderService.positionMapToAreasAndPolygons([area], []);
                }
            });
    }

    private createProjectStub(projectId: number, latitude: number, longitude: number): IProjectStageDto {
        return {
            projectId,
            latitude,
            longitude,
            estateTypes: [],
            saleStateTypes: [],
            projectName: null,
            numberSold: null,
            numUnits: null,
            numStages: null,
            numStagesNotForSale: null,
        };
    }

    private readPositiveInt(name: string): number {
        const value = Number(this.route.snapshot.queryParamMap.get(name));
        return Number.isInteger(value) && value > 0 ? value : null;
    }
}
